import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import MuiDialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import { DeleteOutline } from '@mui/icons-material'
import Autocomplete from '@mui/material/Autocomplete'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useIntl } from 'react-intl'
import es from 'date-fns/locale/es'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import UndoIcon from '@mui/icons-material/Undo'

import feedback from '../../../core/feedback'
import { NOTICES_EDIT_CCAA_PROFILES } from '../constants/notices-edit.constants'
import { getISOStringInitDate } from '../../../core/utils'

const PREFIX = 'PopupEditDialogComponent'

const classes = {
  root: `${PREFIX}-root`,
  dialogPaper: `${PREFIX}-dialogPaper`,
  menuPaper: `${PREFIX}-menuPaper`,
}

const StyledDialog = styled(Dialog)(() => ({
  [`& .${classes.root}`]: {
    margin: 0,
  },
  [`& .${classes.dialogPaper}`]: {
    maxWidth: '100%',
  },
  [`& .${classes.menuPaper}`]: {
    maxHeight: 'calc(50% - 96px)',
  },
}))

const StyledEditorBox = styled(Box)({
  border: '1px solid #ccc',
  borderRadius: '4px',
  padding: '8px',
  '& .ProseMirror': {
    outline: 'none !important',
    '&:focus': {
      outline: 'none !important',
    },
    '&:focus-visible': {
      outline: 'none !important',
    },
  },
})

const DialogTitle = (props) => {
  const { children, onClose, users_reached = '', created_at = null, ...other } = props
  const intl = useIntl()
  const { formatMessage } = intl
  return (
    <MuiDialogTitle sx={{ m: 0, p: 2 }} {...other}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px' }}>
        <div>
          <Typography variant="h6" align="center" color="primary" style={{ marginTop: 8 }}>
            <i>{children}</i>
          </Typography>
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
        {users_reached ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: 10,
            }}
          >
            <Tooltip title={formatMessage({ id: 'pages.notices.popup.edit.users.tooltip' })}>
              <Typography
                variant="body1"
                color="textSecondary"
                display="inline"
                style={{ marginLeft: '10px' }}
              >
                {users_reached}
              </Typography>
            </Tooltip>
            <Typography
              variant="body1"
              color="textSecondary"
              display="inline"
              style={{ paddingRight: '20px' }}
            >
              {formatMessage({ id: 'pages.notices.popup.created.title' })}{' '}
              {new Date(created_at).toLocaleString().substring(0, 10)}
            </Typography>
          </div>
        ) : null}
      </div>
    </MuiDialogTitle>
  )
}

const Toolbar = ({ editor }) => {
  if (!editor) {
    return null
  }

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: '4px',
        p: 1,
        mb: 1,
        display: 'flex',
        gap: '5px',
      }}
    >
      <IconButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        color={editor.isActive('bold') ? 'primary' : 'default'}
        size="small"
      >
        <FormatBoldIcon />
      </IconButton>
      <IconButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        color={editor.isActive('italic') ? 'primary' : 'default'}
        size="small"
      >
        <FormatItalicIcon />
      </IconButton>
      <IconButton onClick={() => editor.chain().focus().undo().run()} size="small">
        <UndoIcon />
      </IconButton>
    </Box>
  )
}

let enddate = new Date()
enddate.setMonth(enddate.getMonth() + 1)

export const PopupEditDialogComponent = ({
  children,
  id = '',
  title = '',
  text = '',
  links = [],
  tags = [],
  onClose = () => {},
  onEdit = () => {},
  open = false,
  tagsList = [],
  profilesSelected = [],
  profilesList = [],
  onDelete = () => {},
  startDate = new Date(),
  endDate = enddate,
  created_at = new Date(),
  users_reached = null,
  ccaa = [],
  ccaaList = [],
}) => {
  const [titleValue, setTitleValue] = useState('')
  const [textValue, setTextValue] = useState('')
  const [linksValue, setLinksValue] = useState([])
  const [tagsValue, setTagsValue] = useState([])
  const [startDateValue, setStartDateValue] = useState(null)
  const [profilesSelectedValue, setProfilesSelectedValue] = useState([])
  const [endDateValue, setEndDateValue] = useState(null)
  const [ccaaValue, setCcaaValue] = useState([])
  const [showCCAA, setShowCCAA] = useState(false)
  const [charCounts, setCharCounts] = useState({})
  const [errorMessages, setErrorMessages] = useState({})
  const [tagsListDropdown, setTagsListDropdown] = useState([])

  const intl = useIntl()
  const { formatMessage } = intl

  const editor = useEditor({
    extensions: [StarterKit],
    content: textValue,
    onUpdate: ({ editor }) => {
      setTextValue(editor.getHTML())
    },
  })

  useEffect(() => {
    if (open) {
      setTitleValue(title)
      setTextValue(text)
      setLinksValue(links)
      setTagsValue(tags)
      setStartDateValue(startDate)
      setProfilesSelectedValue(profilesSelected)
      setEndDateValue(endDate)

      const stringCcaa = ccaa.map((item) => item.toString())
      setCcaaValue(stringCcaa)

      const needed = profilesSelected.some((profile) =>
        NOTICES_EDIT_CCAA_PROFILES.includes(profile)
      )
      setShowCCAA(needed)

      setTagsListDropdown(tagsList.map((tag) => tag.value))

      if (editor && !editor.isDestroyed) {
        if (editor.getHTML() !== text) {
          editor.commands.setContent(text || '')
        }
      }
    }
  }, [open, id])

  function stringArrayToIntArray(array) {
    return array.map((item) => parseInt(item))
  }

  function addLink() {
    if (linksValue.length < 10) {
      setLinksValue(linksValue.concat([{ key: '', value: '' }]))
    }
  }

  const handleUsersChange = (event) => {
    const { value } = event.target
    const newValue = typeof value === 'string' ? value.split(',') : value
    setProfilesSelectedValue(newValue)
    const isNeeded = newValue.some((profile) => NOTICES_EDIT_CCAA_PROFILES.includes(profile))
    setShowCCAA(isNeeded)
  }

  const handleCcaaChange = (event) => {
    const { value } = event.target
    setCcaaValue(typeof value === 'string' ? value.split(',') : value)
  }

  function getProfileText(value) {
    if (profilesList) {
      const elem = profilesList.find((profile) => profile.key === value)
      return elem ? elem.value : ''
    }
    return ''
  }

  function getCCAAText(key) {
    if (ccaaList) {
      const elem = ccaaList.find((ccaa) => ccaa.key === key)
      return elem ? elem.value : ''
    }
    return ''
  }

  function deleteNotice() {
    if (window.confirm(formatMessage({ id: 'pages.notices.popup.edit.delete.warning' }))) {
      onDelete()
    }
  }

  function validateData() {
    let valid = true
    if (!titleValue) valid = false
    if (!textValue || textValue === '<p></p>') valid = false
    if (linksValue.some((link) => !link.key || !link.value)) valid = false
    if (profilesSelectedValue.length === 0) valid = false
    if (showCCAA && ccaaValue.length === 0) valid = false
    if (!startDateValue || !endDateValue) valid = false
    if (tagsValue.length > 10) valid = false
    return valid
  }

  function save() {
    if (validateData()) {
      const payload = {
        title: titleValue,
        body: textValue,
        links: linksValue,
        tags: tagsValue,
        target_users: profilesSelectedValue,
        start_date: startDateValue,
        end_date: endDateValue,
        ccaa: stringArrayToIntArray(ccaaValue),
      }
      onEdit(payload)
    } else {
      feedback('error', formatMessage({ id: 'calls.notices.form.error.description' }))
    }
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle onClose={onClose} users_reached={users_reached} created_at={created_at}>
        {title === ''
          ? formatMessage({ id: 'pages.notices.filters.header.create.button' })
          : formatMessage({ id: 'pages.notices.filters.header.edit.button' })}
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '15px', p: 3 }}>
          <TextField
            id="outlined-basic"
            label={formatMessage({ id: 'pages.notices.popup.edit.title.title' })}
            variant="outlined"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            inputProps={{ maxLength: 255 }}
            fullWidth
            error={!titleValue}
          />

          <Toolbar editor={editor} />
          <StyledEditorBox>
            <EditorContent editor={editor} />
          </StyledEditorBox>

          <div>
            <div style={{ display: 'flex', width: '100%', gap: '5px', alignItems: 'center' }}>
              <h5>{formatMessage({ id: 'pages.notices.popup.edit.links.title' })}</h5>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={addLink}
                style={{ marginBottom: '0', marginLeft: '1rem' }}
              >
                {formatMessage({ id: 'pages.notices.popup.edit.links.add' })}
              </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px' }}>
              {linksValue.map((link, index) => (
                <div
                  style={{ display: 'flex', width: '100%', gap: '5px', alignItems: 'center' }}
                  key={index}
                >
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                    {charCounts[index] || 0}/500
                  </Typography>
                  <TextField
                    label={formatMessage({ id: 'pages.notices.popup.edit.link.text' })}
                    error={link.value === ''}
                    defaultValue={link.value}
                    inputProps={{ maxLength: 500 }}
                    variant="outlined"
                    size="small"
                    onChange={(event) => {
                      const newValue = event.target.value
                      setCharCounts((prev) => ({ ...prev, [index]: newValue.length }))
                      setLinksValue(
                        linksValue.map((l, i) => (i === index ? { ...l, value: newValue } : l))
                      )
                    }}
                    style={{ flex: 1 }}
                  />
                  <TextField
                    label={formatMessage({ id: 'pages.notices.popup.edit.links.url' })}
                    defaultValue={link.key}
                    variant="outlined"
                    error={
                      !!errorMessages[index] || !link.key || !link.key.match(/^https?:\/\/[^ "]+$/)
                    }
                    helperText={errorMessages[index] || ''}
                    inputProps={{ maxLength: 256 }}
                    size="small"
                    onChange={(event) => {
                      const newValue = event.target.value
                      setErrorMessages((prev) => ({ ...prev, [index]: '' }))
                      setLinksValue(
                        linksValue.map((l, i) => (i === index ? { ...l, key: newValue } : l))
                      )
                    }}
                    style={{ flex: 1 }}
                  />
                  <IconButton
                    style={{ color: '#E53A16', height: '30px' }}
                    onClick={() => setLinksValue(linksValue.filter((_, i) => i !== index))}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>

          <FormControl fullWidth error={!profilesSelectedValue.length}>
            <InputLabel>
              {formatMessage({ id: 'pages.notices.popup.edit.target_users.title' })}
            </InputLabel>
            <Select
              multiple
              value={profilesSelectedValue}
              onChange={handleUsersChange}
              input={<Input />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={getProfileText(value)} />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                  },
                },
              }}
            >
              {profilesList.map((profile) => (
                <MenuItem key={profile.key} value={profile.key}>
                  <Checkbox checked={profilesSelectedValue.includes(profile.key)} />
                  <ListItemText primary={profile.value} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showCCAA && (
            <FormControl fullWidth error={!ccaaValue.length}>
              <InputLabel>
                {formatMessage({ id: 'pages.notices.popup.edit.ccaa.title' })}
              </InputLabel>
              <Select
                multiple
                value={ccaaValue}
                onChange={handleCcaaChange}
                input={<Input />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={getCCAAText(value)} />
                    ))}
                  </Box>
                )}
              >
                {ccaaList.map((ccaa) => (
                  <MenuItem key={ccaa.key} value={ccaa.key}>
                    <Checkbox checked={ccaaValue.includes(ccaa.key)} />
                    <ListItemText primary={ccaa.value} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <DatePicker
                  label={formatMessage({ id: 'pages.notices.popup.edit.start_date.title' })}
                  maxDate={endDateValue ? new Date(endDateValue) : undefined}
                  value={startDateValue ? new Date(startDateValue) : null}
                  onChange={(value) => setStartDateValue(getISOStringInitDate(value))}
                  slotProps={{ textField: { fullWidth: true, error: !startDateValue } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <DatePicker
                  label={formatMessage({ id: 'pages.notices.popup.edit.end_date.title' })}
                  minDate={startDateValue ? new Date(startDateValue) : undefined}
                  value={endDateValue ? new Date(endDateValue) : null}
                  onChange={(value) => setEndDateValue(getISOStringInitDate(value))}
                  slotProps={{ textField: { fullWidth: true, error: !endDateValue } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Autocomplete
            multiple
            freeSolo={tagsValue.length < 10}
            options={tagsListDropdown}
            getOptionDisabled={() => tagsValue.length >= 10}
            defaultValue={tagsValue}
            onChange={(event, newValue) => setTagsValue(newValue)}
            clearOnBlur
            filterSelectedOptions
            style={{ marginTop: '20px' }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={formatMessage({ id: 'pages.notices.popup.edit.tags.title' })}
                placeholder={
                  tagsValue.length < 10
                    ? formatMessage({ id: 'pages.notices.popup.edit.tags.placeholder.ok' })
                    : formatMessage({ id: 'pages.notices.popup.edit.tags.placeholder.ko' })
                }
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            padding: '10px',
          }}
        >
          <Tooltip title={formatMessage({ id: 'global.delete' })}>
            <IconButton
              aria-label="delete"
              style={{ color: '#E53A16' }}
              onClick={deleteNotice}
              size="large"
            >
              <DeleteOutline />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={save}>
            {formatMessage({ id: 'global.save' })}
          </Button>
        </div>
      </DialogActions>
    </StyledDialog>
  )
}
